import React, { useCallback, useEffect, useState } from 'react';
import {
  getFyersStatus, getFyersAuthUrl, refreshFyersToken, disconnectFyers,
  getMarketDataSource, setMarketDataSource,
} from '../../services/marketJobs';

/**
 * Admin Market Data panel.
 *
 * Two jobs:
 *   1. Switch the active market-data source at runtime. This is why the toggle
 *      lives in Postgres rather than an env var — rollback is one click with no
 *      deploy, which matters when the alternative is a live-data incident.
 *   2. Manage the Fyers connection. The access token expires every morning
 *      (auto-refreshed), but the REFRESH TOKEN expires roughly every 15 days and
 *      needs a human to re-authorise in a browser. That deadline is surfaced
 *      prominently because nothing can automate it away.
 */

const POLL_MS = 15000;

function Row({ label, children, hint }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid #eee' }}>
      <div>
        <div style={{ fontSize: 13, color: '#444' }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, textAlign: 'right' }}>{children}</div>
    </div>
  );
}

function Pill({ tone, children }) {
  const tones = {
    ok: { bg: '#e7f6ec', fg: '#0a7d32' },
    warn: { bg: '#fff4e5', fg: '#b26a00' },
    bad: { bg: '#fdecea', fg: '#b3261e' },
    idle: { bg: '#eef0f3', fg: '#555' },
  };
  const t = tones[tone] || tones.idle;
  return (
    <span style={{ background: t.bg, color: t.fg, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
      {children}
    </span>
  );
}

function fmtDate(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
}

export default function AdminMarketData({ token }) {
  const [status, setStatus] = useState(null);
  const [sourceInfo, setSourceInfo] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, src] = await Promise.all([
        getFyersStatus(token).catch(e => ({ error: e })),
        getMarketDataSource(token).catch(e => ({ error: e })),
      ]);
      if (!s.error) setStatus(s.data?.data || null);
      if (!src.error) setSourceInfo(src.data?.data || null);
    } catch { /* transient; the next poll retries */ }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const act = async (name, fn) => {
    setBusy(name); setMsg(null);
    try {
      const res = await fn();
      setMsg({ tone: 'ok', text: res.data?.data?.message || 'Done.' });
      await load();
    } catch (err) {
      setMsg({ tone: 'bad', text: err.response?.data?.message || err.message });
    } finally {
      setBusy(null);
    }
  };

  const connect = async () => {
    setBusy('connect'); setMsg(null);
    try {
      const res = await getFyersAuthUrl(token);
      const url = res.data?.data?.auth_url;
      if (!url) throw new Error('No auth URL returned');
      // New tab: the flow ends on a Fyers-hosted page that redirects back to our
      // callback, so it must not replace the admin panel.
      window.open(url, '_blank', 'noopener');
      setMsg({
        tone: 'warn',
        text: 'Fyers login opened in a new tab. Complete it there, then return here — the status below will update.',
      });
    } catch (err) {
      setMsg({ tone: 'bad', text: err.response?.data?.message || err.message });
    } finally {
      setBusy(null);
    }
  };

  const auth = status?.auth;
  const src = sourceInfo?.source;
  const activeSource = src?.value;

  const authTone = !auth ? 'idle'
    : auth.status === 'CONNECTED' && auth.access_token_valid ? 'ok'
    : auth.status === 'DISCONNECTED' ? 'idle' : 'bad';

  const refreshTone = auth?.refresh_warning === 'WARNING' ? 'warn'
    : auth?.refresh_warning ? 'bad' : 'ok';

  return (
    <div style={{ maxWidth: 880 }}>
      <h2 style={{ marginBottom: 4 }}>Market Data</h2>
      <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
        Choose where quotes and charts are served from, and manage the Fyers connection.
      </p>

      {msg && (
        <div style={{
          margin: '12px 0', padding: '10px 12px', borderRadius: 8, fontSize: 13,
          background: msg.tone === 'ok' ? '#e7f6ec' : msg.tone === 'warn' ? '#fff4e5' : '#fdecea',
          color: msg.tone === 'ok' ? '#0a7d32' : msg.tone === 'warn' ? '#b26a00' : '#b3261e',
        }}>{msg.text}</div>
      )}

      {/* ── Source ─────────────────────────────────────────────────────────── */}
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 10px', fontSize: 15 }}>Active source</h3>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          {['yahoo', 'fyers'].map(opt => (
            <button
              key={opt}
              disabled={busy === 'source' || activeSource === opt}
              onClick={() => act('source', () => setMarketDataSource(token, opt))}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                border: activeSource === opt ? '2px solid #0a7d32' : '1px solid #d0d5dd',
                background: activeSource === opt ? '#f0fbf4' : '#fff',
                fontWeight: activeSource === opt ? 600 : 400,
              }}
            >
              {opt === 'yahoo' ? 'Yahoo (stored candles)' : 'Fyers (live)'}
              <div style={{ fontSize: 11, color: '#666', fontWeight: 400, marginTop: 4 }}>
                {opt === 'yahoo'
                  ? 'Ingested on a schedule and read from storage. Always available.'
                  : 'Live passthrough, nothing stored. Requires a connected Fyers account.'}
              </div>
            </button>
          ))}
        </div>

        {activeSource === 'fyers' && (
          <div style={{ fontSize: 12, color: '#b26a00', background: '#fff4e5', padding: '8px 10px', borderRadius: 6 }}>
            Live data does not rewrite the stored Yahoo candles. Any Fyers failure —
            an expired token, an outage, or an exhausted rate budget — falls back to
            them automatically, and the API reports <code>degraded: true</code> so the
            UI can badge it.
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <Row label="Changed by" hint="Effective within ~5 minutes without a deploy">
            {src?.updated_by || '—'} · {fmtDate(src?.updated_at)}
          </Row>
          <Row label="Fallbacks to Yahoo" hint="A rising count means the live source is unhealthy">
            {sourceInfo?.dispatch?.fallbacks ?? 0}
            {sourceInfo?.dispatch?.lastFallback && (
              <div style={{ fontSize: 11, color: '#888' }}>
                last: {sourceInfo.dispatch.lastFallback.reason}
              </div>
            )}
          </Row>
          <Row label="Cache hit rate" hint="Higher means fewer upstream calls per user">
            {sourceInfo?.cache?.hit_rate != null ? `${(sourceInfo.cache.hit_rate * 100).toFixed(1)}%` : '—'}
          </Row>
          <Row label="Rate budget (this minute)" hint="Three breaches in one day blocks the app for the rest of the day">
            {status?.rate_limit?.minute
              ? `${status.rate_limit.minute.used} / ${status.rate_limit.minute.limit}`
              : '—'}
          </Row>
        </div>
      </section>

      {/* ── Fyers connection ───────────────────────────────────────────────── */}
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Fyers connection</h3>
          <Pill tone={authTone}>{auth?.status || 'UNKNOWN'}</Pill>
        </div>

        {auth && !auth.configured && (
          <div style={{ fontSize: 12, color: '#b3261e', background: '#fdecea', padding: '8px 10px', borderRadius: 6, marginBottom: 12 }}>
            Not configured. Set these environment variables and restart:{' '}
            <code>{(auth.missing_env || []).join(', ')}</code>
          </div>
        )}

        {auth?.refresh_warning && (
          <div style={{
            fontSize: 12, marginBottom: 12, padding: '8px 10px', borderRadius: 6,
            background: auth.refresh_warning === 'WARNING' ? '#fff4e5' : '#fdecea',
            color: auth.refresh_warning === 'WARNING' ? '#b26a00' : '#b3261e',
          }}>
            <strong>Re-authorisation needed{auth.refresh_warning === 'EXPIRED' ? ' now' : ' soon'}.</strong>{' '}
            The refresh token {auth.refresh_warning === 'EXPIRED' ? 'has expired' :
              `expires in ${auth.refresh_token_days_left} day(s)`}. This cannot be automated —
            click <em>Connect Fyers</em> and log in again.
          </div>
        )}

        <Row label="Access token" hint="Expires each morning (~06:00 IST); refreshed automatically">
          {auth?.access_token_valid ? <Pill tone="ok">valid</Pill> : <Pill tone="bad">invalid</Pill>}
          <div style={{ fontSize: 11, color: '#888' }}>{fmtDate(auth?.access_token_expires_at)}</div>
        </Row>
        <Row label="Refresh token" hint="~15 day lifetime; needs a human to renew">
          <Pill tone={refreshTone}>
            {auth?.refresh_token_days_left != null ? `${auth.refresh_token_days_left}d left` : '—'}
          </Pill>
        </Row>
        <Row label="Token cached for hot reads">{auth?.token_in_redis ? 'yes' : 'no'}</Row>
        <Row label="Last refresh">
          {auth?.last_refresh_status || '—'}
          <div style={{ fontSize: 11, color: '#888' }}>{fmtDate(auth?.last_refresh_at)}</div>
          {auth?.last_refresh_error && (
            <div style={{ fontSize: 11, color: '#b3261e' }}>{auth.last_refresh_error}</div>
          )}
        </Row>
        <Row label="Symbol master" hint="Public Fyers file; refreshed daily, costs no rate budget">
          {status?.symbol_master?.counts
            ? `${status.symbol_master.counts.nseEquity} NSE equities, ${status.symbol_master.counts.nseIndex} indices`
            : (status?.symbol_master?.error || '—')}
        </Row>

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <button onClick={connect} disabled={busy === 'connect' || !auth?.configured}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0a7d32', color: '#fff', cursor: 'pointer' }}>
            {busy === 'connect' ? 'Opening…' : 'Connect Fyers'}
          </button>
          <button onClick={() => act('refresh', () => refreshFyersToken(token))}
            disabled={busy === 'refresh' || !auth?.has_refresh_token}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#fff', cursor: 'pointer' }}>
            {busy === 'refresh' ? 'Refreshing…' : 'Refresh token now'}
          </button>
          <button onClick={() => act('disconnect', () => disconnectFyers(token))}
            disabled={busy === 'disconnect' || auth?.status === 'DISCONNECTED'}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #f2b8b5', background: '#fff', color: '#b3261e', cursor: 'pointer' }}>
            Disconnect
          </button>
        </div>
      </section>

      {/* ── Realtime ───────────────────────────────────────────────────────── */}
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Realtime feed</h3>
          <Pill tone={sourceInfo?.realtime?.enabled ? (sourceInfo.realtime.upstream?.connected ? 'ok' : 'warn') : 'idle'}>
            {!sourceInfo?.realtime?.enabled ? 'disabled'
              : sourceInfo.realtime.upstream?.connected ? 'connected' : 'not connected'}
          </Pill>
        </div>

        {!sourceInfo?.realtime?.enabled && (
          <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
            Set <code>REALTIME_ENABLED=true</code> to stream live prices. Without it,
            order fills and stop-losses use the latest 1-minute close, which can be
            up to a minute stale.
          </div>
        )}

        <Row label="Upstream connection" hint="One socket serves all users and costs no rate budget">
          {sourceInfo?.realtime?.upstream?.connected ? 'connected' : 'down'}
        </Row>
        <Row label="Symbols subscribed">{sourceInfo?.realtime?.upstream?.subscribed_symbols ?? '—'}</Row>
        <Row label="Seconds since last tick" hint="Rising during market hours means a stalled feed">
          {sourceInfo?.realtime?.upstream?.seconds_since_last_tick ?? '—'}
        </Row>
        <Row label="Browser clients">
          {sourceInfo?.realtime?.browser?.clients ?? '—'} / {sourceInfo?.realtime?.browser?.max_clients ?? '—'}
        </Row>
        <Row label="Ticks received">{sourceInfo?.realtime?.upstream?.ticks ?? '—'}</Row>
      </section>
    </div>
  );
}
